// Standalone store for the practice Metronome tab's saved presets. Kept
// deliberately separate from AppStore (songs/setlists) since the standalone
// metronome is intentionally independent of the performance/setlist system —
// it shares only the underlying IndexedDB connection and the MetronomeEngine.

import { generateId } from './id';
import { database } from '../db/database';
import { DEFAULT_METRONOME_SETTINGS, type MetronomePresetRecord, type MetronomeSettings } from './types';

type Listener = () => void;

class MetronomePresetStore {
  private presets: MetronomePresetRecord[] = [];
  private loaded = false;
  private listeners = new Set<Listener>();

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notify(): void {
    for (const listener of this.listeners) listener();
  }

  getPresets(): MetronomePresetRecord[] {
    return this.presets;
  }

  isLoaded(): boolean {
    return this.loaded;
  }

  async load(): Promise<void> {
    this.presets = await database.getAllMetronomePresets();
    this.loaded = true;
    this.notify();
  }

  async savePreset(name: string, settings: MetronomeSettings): Promise<MetronomePresetRecord> {
    const now = Date.now();
    const preset: MetronomePresetRecord = {
      id: generateId(),
      name,
      createdAt: now,
      updatedAt: now,
      settings: { ...settings },
    };
    await database.putMetronomePreset(preset);
    this.presets = [...this.presets, preset];
    this.notify();
    return preset;
  }

  async renamePreset(id: string, newName: string): Promise<void> {
    const preset = this.presets.find((p) => p.id === id);
    if (!preset) return;
    const updated: MetronomePresetRecord = { ...preset, name: newName, updatedAt: Date.now() };
    await database.putMetronomePreset(updated);
    this.presets = this.presets.map((p) => (p.id === id ? updated : p));
    this.notify();
  }

  async updatePresetSettings(id: string, settings: MetronomeSettings): Promise<void> {
    const preset = this.presets.find((p) => p.id === id);
    if (!preset) return;
    const updated: MetronomePresetRecord = { ...preset, settings: { ...settings }, updatedAt: Date.now() };
    await database.putMetronomePreset(updated);
    this.presets = this.presets.map((p) => (p.id === id ? updated : p));
    this.notify();
  }

  async deletePreset(id: string): Promise<void> {
    await database.deleteMetronomePreset(id);
    this.presets = this.presets.filter((p) => p.id !== id);
    this.notify();
  }
}

export const metronomePresetStore = new MetronomePresetStore();
export const DEFAULT_STANDALONE_METRONOME_SETTINGS: MetronomeSettings = { ...DEFAULT_METRONOME_SETTINGS };
